import { markdownLanguage } from "@codemirror/lang-markdown";
import { attachmentReference, resolveAttachmentPath } from "./vault/attachments";

interface ImageSyntaxNode {
  name: string;
  from: number;
  to: number;
  firstChild: ImageSyntaxNode | null;
  nextSibling: ImageSyntaxNode | null;
}

export interface ParsedMarkdownImage {
  from: number;
  to: number;
  altFrom: number;
  altTo: number;
  urlFrom: number;
  urlTo: number;
  rawAlt: string;
  alt: string;
  src: string;
  width: number | null;
}

export function parseImageAlt(raw: string): { rawAlt: string; alt: string; width: number | null } {
  const marker = /\|([1-9]\d{1,3})$/.exec(raw);
  const slashCount = marker
    ? /\\*$/.exec(raw.slice(0, marker.index))?.[0].length ?? 0 : 0;
  const width = marker && slashCount % 2 === 0 && Number(marker[1]) <= 2000
    ? Number(marker[1]) : null;
  const rawAlt = width === null ? raw : raw.slice(0, marker!.index);
  return {
    rawAlt,
    alt: rawAlt.replace(/\\([\\\[\]\|])/g, "$1"),
    width,
  };
}

export function parseMarkdownImage(node: ImageSyntaxNode, source: string): ParsedMarkdownImage | null {
  if (node.name !== "Image") return null;
  const children: ImageSyntaxNode[] = [];
  for (let child = node.firstChild; child; child = child.nextSibling) children.push(child);
  const closeAlt = children.find((child) => child.name === "LinkMark"
    && source.slice(child.from, child.to) === "]");
  const url = children.find((child) => child.name === "URL");
  if (!closeAlt || !url) return null;
  const altFrom = node.from + 2;
  const altTo = closeAlt.from;
  const parsed = parseImageAlt(source.slice(altFrom, altTo));
  return {
    from: node.from,
    to: node.to,
    altFrom,
    altTo,
    urlFrom: url.from,
    urlTo: url.to,
    ...parsed,
    src: source.slice(url.from, url.to),
  };
}

/** Alt text on one line, its [ ] | and \ escaped so it can't end the alt or set a width. */
const escapeAlt = (text: string) => text.replace(/[\r\n]+/g, " ").replace(/[\\\[\]\|]/g, "\\$&");

export function imageMarkdown(filename: string, reference: string): string {
  const name = filename.replace(/\.[^.]+$/, "").trim() || "Image";
  return `![${escapeAlt(name)}](${reference})`;
}

/**
 * An image of a web address, or null unless `address` is one (http, https, or www.). Its alt
 * text is `alt`, or else the picture's file name, and ( ) in the address are escaped so the
 * Markdown ends where the address does.
 */
export function imageMarkdownForUrl(address: string, alt = ""): string | null {
  const text = address.trim();
  if (!text || /\s/.test(text)) return null;
  let url: URL;
  try {
    url = new URL(/^www\./i.test(text) ? `https://${text}` : text);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  let file = url.pathname.split("/").filter(Boolean).at(-1) ?? "";
  try {
    file = decodeURIComponent(file);
  } catch {}
  const name = alt.trim() || file.replace(/\.[^.]+$/, "").trim() || "Image";
  return `![${escapeAlt(name)}](${url.href.replace(/\(/g, "%28").replace(/\)/g, "%29")})`;
}

export function imageAltWithWidth(rawAlt: string, width: number): string {
  return `${parseImageAlt(rawAlt).rawAlt}|${Math.round(width)}`;
}

export function rebaseAttachmentImages(source: string, oldNotePath: string, newNotePath: string): string {
  const changes: Array<{ from: number; to: number; insert: string }> = [];
  markdownLanguage.parser.parse(source).iterate({
    enter(node) {
      if (node.name !== "Image") return;
      const image = parseMarkdownImage(node.node, source);
      if (!image) return false;
      const asset = resolveAttachmentPath(oldNotePath, image.src);
      if (!asset) return false;
      const insert = attachmentReference(newNotePath, asset);
      if (insert !== image.src) changes.push({ from: image.urlFrom, to: image.urlTo, insert });
      return false;
    },
  });
  for (const change of changes.reverse()) {
    source = source.slice(0, change.from) + change.insert + source.slice(change.to);
  }
  return source;
}
